import { AxiosResponse, isAxiosError } from 'axios'
import api from './api'
import {
	AuthLoginUrlOptions,
	AuthLogoutUrlOptions,
	GetAuthMeResponseData,
} from './types/auth.interfaces'
import { getCurrentReturnTo, toAbsoluteReturnTo } from '../utils/authRedirect'
import { isPublicAuthExcludedPathname } from '../utils/authPath'

const URL = '/auth'
const LAST_ACTIVITY_KEY = 'lastActivity'
const INACTIVITY_SESSION_KEY = 'auth:inactivitySession'
const AUTH_ME_TIMEOUT_MS = 15000

let loginRedirectStarted = false

export const isLoginRedirectStarted = () => loginRedirectStarted

const clearInactivityTracking = () => {
	try {
		localStorage.removeItem(LAST_ACTIVITY_KEY)
		sessionStorage.removeItem(INACTIVITY_SESSION_KEY)
	} catch {
		// Ignore storage failures during auth redirects.
	}
}

const getAuthApiBaseUrl = () =>
	(process.env.REACT_APP_BACKEND_API || '').replace(/\/+$/, '')

const buildLoginUrl = ({ returnTo, promptLogin }: AuthLoginUrlOptions = {}) => {
	const params = new URLSearchParams({
		returnTo: toAbsoluteReturnTo(returnTo ?? getCurrentReturnTo()),
	})
	if (promptLogin) params.set('prompt', 'login')

	return `${getAuthApiBaseUrl()}${URL}/login?${params.toString()}`
}

export const isUnauthorizedError = (error: unknown) => {
	if (!isAxiosError(error)) return false
	const status = error.response?.status

	// Only 401 is unauthorized. Network/CORS errors (no status) are not auth issues.
	return status === 401
}

let sessionCheckInFlight: Promise<void> | null = null

export const AuthService = {
	async getMe(): Promise<GetAuthMeResponseData> {
		const response: AxiosResponse<GetAuthMeResponseData> =
			await api.get<GetAuthMeResponseData>(`${URL}/me`, {
				timeout: AUTH_ME_TIMEOUT_MS,
			})

		return response.data
	},

	login(options: AuthLoginUrlOptions = {}) {
		if (loginRedirectStarted || typeof window === 'undefined') return

		loginRedirectStarted = true
		clearInactivityTracking()
		window.location.replace(buildLoginUrl(options))
	},

	logout(_options: AuthLogoutUrlOptions = {}) {
		if (typeof window === 'undefined') return

		// Native app re-injects isApp on load; preserving avoids a gap before inject.
		try {
			const isApp = localStorage.getItem('isApp')
			localStorage.clear()
			if (isApp !== null) localStorage.setItem('isApp', isApp)
			sessionStorage.removeItem(INACTIVITY_SESSION_KEY)
		} catch {}

		window.location.replace(
			`${getAuthApiBaseUrl()}${URL}/logout?returnTo=${process.env.REACT_APP_LOGOUT_REDIRECT_URI}?logout=1`,
		)
	},

	async revalidateSessionOrLogin(): Promise<void> {
		if (loginRedirectStarted) return
		if (sessionCheckInFlight) return sessionCheckInFlight

		sessionCheckInFlight = (async () => {
			try {
				await this.getMe()
			} catch (meError) {
				if (isUnauthorizedError(meError)) {
					this.login()
				}
			} finally {
				sessionCheckInFlight = null
			}
		})()

		return sessionCheckInFlight
	},
}

if (typeof window !== 'undefined') {
	window.addEventListener('pageshow', (event) => {
		if (!event.persisted) return
		if (isPublicAuthExcludedPathname(window.location.pathname)) return

		loginRedirectStarted = false
		void AuthService.revalidateSessionOrLogin()
	})
}
